import { debounce } from "lodash";
import { useEffect, useState } from "react";
import {
  useCandidateScheduledMessages,
  useDeleteScheduledMessage,
} from "../../../../App/Queries/MessageQueries";
import { IGroupMessageView } from "../../../../App/Typings/MessageText";
import { ITableColumn } from "../../../../App/Typings/TableColumn";

const defaultMsg: IGroupMessageView = {
  create: new Date(),
  messageDisplay: "",
  messageTextId: "",
  messageTrackingId: "",
  scheduledTimeEaster: "",
  userName: "",
  type: "",
  messageState: "",
  userId: "",
  recipient: "",
  recipientId: "",
};
const errorMessagesForEmails = new Map<string, string>([
  ["TimeFrameException", "Time requested to send is outside configured rules."],
  ["InvalidCandidate", "Candidate was not found."],
  ["NoCandidateEmailsFound", "Unable to find candidate email addresses."],
  ["InvalidTimeConfiguration", "Configuration missing for allowed hours and/or allowed minutes."],
  ["NotInGroup", "Candidate not longer in group at time of message."],
  ["Unsubscribed", "Candidate is unsubscribed from email."],
  ["CanEmail", "Candidate has not been enabled to send emails."],
  ["EmailNotValid", "Email is not valid for candidate."],
  ["EmailAddress", "Unable to locate candidate email address."],
  ["Stop", "Candidate is unsubscribed from email."],
]);

const columnsWithActionButtons: ITableColumn[] = [
  { label: "Type", propName: "type", order: 0 },
  { label: "State", propName: "messageState", order: 1 },
  { label: "Scheduled", propName: "scheduledUtcTime", order: 2 },
  { label: "Message", propName: "messageDisplay", order: 3 },
  { label: "Actions", propName: "actions", order: 4 },
];
const columnsNoActionButtons: ITableColumn[] = [
  { label: "Type", propName: "type", order: 0 },
  { label: "State", propName: "messageState", order: 1 },
  { label: "Time Stamp", propName: "scheduledUtcTime", order: 2 },
  { label: "Message", propName: "messageDisplay", order: 3 },
];

const columnsNoActionButtonsForErrors: ITableColumn[] = [
  { label: "Type", propName: "type", order: 0 },
  { label: "Error Description", propName: "messageState", order: 1 },
  { label: "Time Stamp", propName: "scheduledUtcTime", order: 2 },
  { label: "Message", propName: "messageDisplay", order: 3 },
];

export default function MessageEmailHistoryTabState(candidateId: string) {
  const [tabValue, setTabValue] = useState<number>(0);
  const { data: scheduledMessages, isLoading: isLoadingScheduledMessages } =
    useCandidateScheduledMessages(candidateId);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [actionMessage, setActionMessage] = useState<IGroupMessageView>(defaultMsg);

  const [schMessages, setScheduledMessages] = useState<IGroupMessageView[]>(
    scheduledMessages?.groupScheduledMessages ?? [],
  );
  const [sentMessages, setSentMessages] = useState<IGroupMessageView[]>(
    scheduledMessages?.groupDeliveredMessages ?? [],
  );
  const [errorMessages, setErrorMessages] = useState<IGroupMessageView[]>(
    scheduledMessages?.groupErrorMessages ?? [],
  );
  const [receivedMessages, setReceivedMessages] = useState<IGroupMessageView[]>(
    scheduledMessages?.groupReceivedMessages ?? [],
  );

  useEffect(() => {
    if (scheduledMessages && !isLoadingScheduledMessages) {
      setScheduledMessages(scheduledMessages?.groupScheduledMessages ?? []);
      setSentMessages(scheduledMessages?.groupDeliveredMessages ?? []);
      setErrorMessages(scheduledMessages?.groupErrorMessages ?? []);
      setReceivedMessages(scheduledMessages?.groupReceivedMessages ?? []);
    }
  }, [scheduledMessages, isLoadingScheduledMessages]);

  const { mutate: deleteScheduledMessage, isLoading: deleteScheduledMessageIsLoading } =
    useDeleteScheduledMessage();

  const handleConfirmationResponse = (msg: IGroupMessageView, response: boolean) => {
    if (response) {
      setScheduledMessages((messages) => messages.filter((listMsg) => listMsg !== msg));
      handleConfirmDeleteSelectedScheduledMessages(msg);
    }
    setShowConfirmDialog(false);
  };

  const handleConfirmDeleteSelectedScheduledMessages = debounce((msg: IGroupMessageView) => {
    deleteScheduledMessage(msg.messageTrackingId, {
      onSuccess: () => {
        setScheduledMessages((messages) => messages.filter((listMsg) => listMsg !== msg));
      },
    });
  }, 300);

  const deleteSelectedScheduledMessages = (msg: IGroupMessageView) => {
    setActionMessage(msg);
    setShowConfirmDialog(true);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    setScheduledMessages(scheduledMessages?.groupScheduledMessages ?? []);
    setSentMessages(scheduledMessages?.groupDeliveredMessages ?? []);
    setErrorMessages(scheduledMessages?.groupErrorMessages ?? []);
    setReceivedMessages(scheduledMessages?.groupReceivedMessages ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingScheduledMessages]);

  return {
    getters: {
      columnsNoActionButtons,
      columnsWithActionButtons,
      columnsNoActionButtonsForErrors,
      schMessages,
      showConfirmDialog,
      actionMessage,
      tabValue,
      isLoadingScheduledMessages,
      sentMessages,
      errorMessages,
      receivedMessages,
      deleteScheduledMessageIsLoading,
      errorMessagesForEmails,
    },
    setters: {
      setScheduledMessages,
      setShowConfirmDialog,
      setActionMessage,
      setSentMessages,
      setErrorMessages,
    },
    handlers: {
      deleteSelectedScheduledMessages,
      handleConfirmationResponse,
      handleChange,
    },
  };
}
